import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import RefreshButton from "@/components/v2/RefreshButton";
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  Trophy,
  Target,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  FileText,
  Filter,
  RefreshCw,
  ChevronRight,
  Zap,
  Award,
  BarChart3,
  Eye,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const BidPriorityDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    category: '',
    minScore: '',
    maxScore: ''
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.category) params.append('category', filters.category);
      if (filters.minScore) params.append('min_score', filters.minScore);
      if (filters.maxScore) params.append('max_score', filters.maxScore);

      const response = await axios.get(`${API}/api/v2/bid-priority-dashboard?${params.toString()}`);
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch bid priority data:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    fetchDashboardData();
  };

  const clearFilters = () => {
    setFilters({ status: '', category: '', minScore: '', maxScore: '' });
    setTimeout(fetchDashboardData, 100);
  };

  const getPriorityBadgeClass = (level) => {
    switch (level) {
      case 'High': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'Medium': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'Low': return 'bg-orange-100 text-orange-700 border-orange-200';
      default: return 'bg-red-100 text-red-700 border-red-200';
    }
  };

  const getScoreColor = (score) => {
    if (score >= 70) return 'text-emerald-600';
    if (score >= 50) return 'text-amber-600';
    if (score >= 30) return 'text-orange-600';
    return 'text-red-600';
  };

  const getProgressColor = (score) => {
    if (score >= 70) return 'bg-emerald-500';
    if (score >= 50) return 'bg-amber-500';
    if (score >= 30) return 'bg-orange-500';
    return 'bg-red-500';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-slate-200 rounded w-1/3"></div>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="h-24 bg-slate-200 rounded-xl"></div>
              ))}
            </div>
            <div className="h-96 bg-slate-200 rounded-xl"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-8" data-testid="bid-priority-dashboard">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-3">
              <Trophy className="w-8 h-8 text-amber-500" />
              Bid Priority Dashboard
            </h1>
            <p className="text-slate-500 mt-1">RFPs ranked by win potential - focus on high-value opportunities</p>
          </div>
          <RefreshButton onClick={fetchDashboardData} data-testid="refresh-dashboard-btn" />
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card className="border-0 shadow-md hover:shadow-lg transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Total RFPs</p>
                  <p className="text-xl font-bold text-slate-800" data-testid="total-rfps-count">
                    {data?.summary?.total_rfps || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md hover:shadow-lg transition-shadow bg-gradient-to-br from-emerald-50 to-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">High Priority</p>
                  <p className="text-xl font-bold text-emerald-600" data-testid="high-priority-count">
                    {data?.summary?.high_priority || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md hover:shadow-lg transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                  <Target className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Medium</p>
                  <p className="text-xl font-bold text-amber-600">
                    {data?.summary?.medium_priority || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md hover:shadow-lg transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
                  <TrendingDown className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Low</p>
                  <p className="text-xl font-bold text-orange-600">
                    {data?.summary?.low_priority || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md hover:shadow-lg transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Review</p>
                  <p className="text-xl font-bold text-red-600">
                    {data?.summary?.review_needed || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md hover:shadow-lg transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Avg Score</p>
                  <p className="text-xl font-bold text-purple-600">
                    {data?.summary?.avg_score || 0}%
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="border-0 shadow-md">
          <CardContent className="p-4">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-medium text-slate-600">Filters:</span>
              </div>
              
              <Select value={filters.status} onValueChange={(v) => setFilters(prev => ({ ...prev, status: v }))}>
                <SelectTrigger className="w-[150px]" data-testid="filter-status">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="analyzed">Analyzed</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="processing">Processing</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filters.category} onValueChange={(v) => setFilters(prev => ({ ...prev, category: v }))}>
                <SelectTrigger className="w-[180px]" data-testid="filter-category">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  {data?.categories?.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filters.minScore} onValueChange={(v) => setFilters(prev => ({ ...prev, minScore: v }))}>
                <SelectTrigger className="w-[140px]" data-testid="filter-min-score">
                  <SelectValue placeholder="Min Score" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">0%+</SelectItem>
                  <SelectItem value="30">30%+</SelectItem>
                  <SelectItem value="50">50%+</SelectItem>
                  <SelectItem value="70">70%+</SelectItem>
                </SelectContent>
              </Select>

              <Button onClick={applyFilters} size="sm" className="bg-[#007AFF]" data-testid="apply-filters-btn">
                Apply
              </Button>
              <Button onClick={clearFilters} variant="ghost" size="sm" data-testid="clear-filters-btn">
                Clear
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Priority List */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-500" />
              RFP Priority Ranking
            </CardTitle>
            <CardDescription>Click on any RFP to view detailed analysis</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data?.rfps?.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Target className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p className="text-lg">No RFPs match the current filters</p>
                  <p className="text-sm mt-1">Try adjusting your filter criteria</p>
                </div>
              ) : (
                data?.rfps?.map((rfp, index) => (
                  <div 
                    key={rfp.rfp_id}
                    className="border border-slate-200 rounded-xl p-4 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer"
                    onClick={() => navigate(`/tender/${rfp.rfp_id}`)}
                    data-testid={`priority-rfp-${rfp.rfp_id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      {/* Left: Rank & Info */}
                      <div className="flex items-start gap-4 flex-1">
                        {/* Rank Badge */}
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-lg ${
                          index === 0 ? 'bg-amber-100 text-amber-700' :
                          index === 1 ? 'bg-slate-200 text-slate-600' :
                          index === 2 ? 'bg-orange-100 text-orange-700' :
                          'bg-slate-100 text-slate-500'
                        }`}>
                          {index + 1}
                        </div>
                        
                        {/* RFP Details */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-slate-800 truncate">{rfp.name}</h3>
                            <Badge className={getPriorityBadgeClass(rfp.priority_level)}>
                              {rfp.priority_level} Priority
                            </Badge>
                          </div>
                          
                          <div className="flex items-center gap-4 text-sm text-slate-500 mb-3">
                            <span className="flex items-center gap-1">
                              <FileText className="w-3 h-3" />
                              {rfp.total_pages || 0} pages
                            </span>
                            <Badge variant="outline" className="text-xs">{rfp.category}</Badge>
                            <Badge variant={rfp.status === 'analyzed' ? 'default' : 'secondary'} className="text-xs">
                              {rfp.status}
                            </Badge>
                          </div>

                          {/* Score Breakdown */}
                          <div className="grid grid-cols-4 gap-2">
                            <div className="text-center p-2 bg-slate-50 rounded-lg">
                              <p className="text-xs text-slate-500">Eligibility</p>
                              <p className={`text-sm font-bold ${getScoreColor(rfp.score_breakdown?.eligibility_match || 0)}`}>
                                {rfp.score_breakdown?.eligibility_match || 0}%
                              </p>
                            </div>
                            <div className="text-center p-2 bg-slate-50 rounded-lg">
                              <p className="text-xs text-slate-500">Technical</p>
                              <p className={`text-sm font-bold ${getScoreColor(rfp.score_breakdown?.technical_match || 0)}`}>
                                {rfp.score_breakdown?.technical_match || 0}%
                              </p>
                            </div>
                            <div className="text-center p-2 bg-slate-50 rounded-lg">
                              <p className="text-xs text-slate-500">Experience</p>
                              <p className={`text-sm font-bold ${getScoreColor(rfp.score_breakdown?.experience_match || 0)}`}>
                                {rfp.score_breakdown?.experience_match || 0}%
                              </p>
                            </div>
                            <div className="text-center p-2 bg-slate-50 rounded-lg">
                              <p className="text-xs text-slate-500">Financial</p>
                              <p className={`text-sm font-bold ${getScoreColor(rfp.score_breakdown?.financial_match || 0)}`}>
                                {rfp.score_breakdown?.financial_match || 0}%
                              </p>
                            </div>
                          </div>

                          {/* Strengths & Gaps */}
                          <div className="flex gap-4 mt-3">
                            {rfp.key_strengths?.length > 0 && (
                              <div className="flex-1">
                                <p className="text-xs text-emerald-600 font-medium mb-1 flex items-center gap-1">
                                  <CheckCircle2 className="w-3 h-3" /> Strengths
                                </p>
                                <div className="flex flex-wrap gap-1">
                                  {rfp.key_strengths.map((s, i) => (
                                    <Badge key={i} variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">
                                      {s.category || s}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                            {rfp.key_gaps?.length > 0 && (
                              <div className="flex-1">
                                <p className="text-xs text-red-600 font-medium mb-1 flex items-center gap-1">
                                  <XCircle className="w-3 h-3" /> Gaps
                                </p>
                                <div className="flex flex-wrap gap-1">
                                  {rfp.key_gaps.map((g, i) => (
                                    <Badge key={i} variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">
                                      {g.category || g}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Right: Score & Win Probability */}
                      <div className="flex flex-col items-end gap-2 w-40">
                        <div className="text-center">
                          <p className={`text-3xl font-bold ${getScoreColor(rfp.overall_score)}`}>
                            {rfp.overall_score}%
                          </p>
                          <p className="text-xs text-slate-500">Bid Score</p>
                        </div>
                        
                        <div className="w-full bg-slate-100 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${getProgressColor(rfp.overall_score)}`}
                            style={{ width: `${rfp.overall_score}%` }}
                          />
                        </div>
                        
                        <div className="flex items-center gap-1 text-sm">
                          {rfp.win_probability >= 50 ? (
                            <ArrowUpRight className="w-4 h-4 text-emerald-500" />
                          ) : (
                            <ArrowDownRight className="w-4 h-4 text-red-500" />
                          )}
                          <span className={rfp.win_probability >= 50 ? 'text-emerald-600' : 'text-red-600'}>
                            {rfp.win_probability}% win chance
                          </span>
                        </div>

                        <Badge className="text-xs bg-blue-50 text-blue-700">
                          {rfp.recommendation}
                        </Badge>

                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-[#007AFF]"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/tender/${rfp.rfp_id}`);
                          }}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BidPriorityDashboard;
